import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useAppStore } from '@/hooks/useAppStore'
import { Atmosphere } from '@/components/shared/Atmosphere'
import { DNA_PROFILES } from '@/data/destinations'
import { submitLead } from '@/services/leads'

// ─── Gate Screen ────────────────────────────────────────────────────────────────
// "Unlock our family adventure" email-capture gate, inserted between
// JourneyScreen and JourneyCardScreen. Posts to /api/lead; never blocks the
// demo on failure. Returning users (leadEmail already stored) skip straight
// through — JourneyScreen only routes here if there's no stored email.

function isValidEmail(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim())
}

export function GateScreen() {
  const { selectedDestination, dnaKey, setScreen, setLeadEmail } = useAppStore()
  const profile = DNA_PROFILES[dnaKey]
  const dest = selectedDestination

  const [email, setEmail] = useState('')
  const [touched, setTouched] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  if (!dest) {
    setScreen('dna')
    return null
  }

  const valid = isValidEmail(email)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setTouched(true)
    if (!valid || submitting) return

    setSubmitting(true)
    setLeadEmail(email.trim())

    // Fire-and-forget: never block progression on API result/failure.
    await submitLead({
      email: email.trim(),
      brand: 'yonder',
      dna: profile?.name ?? dnaKey,
      destination: dest.name,
    })

    setSubmitting(false)
    setScreen('card')
  }

  return (
    <div className="yonder-world flex flex-col min-h-screen">
      <Atmosphere density="low" />

      {/* Header */}
      <div className="relative z-10 flex items-center justify-between px-5 sm:px-8 py-5">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-sun to-sun-light flex items-center justify-center text-lg shadow-glow-sun">
            🧭
          </div>
          <div>
            <div className="text-base font-black leading-none text-white">
              Yon<span className="text-sun-pale">der</span>
            </div>
            <div className="text-[9px] font-semibold text-white/60 leading-none mt-0.5 tracking-widest uppercase">
              by Imaginarium
            </div>
          </div>
        </div>
        <button
          onClick={() => setScreen('journey')}
          className="text-[11px] font-bold text-white/80 bg-white/10 border border-white/20 backdrop-blur-sm px-4 py-1.5 rounded-full hover:bg-white/20 transition-colors"
        >
          ← Back
        </button>
      </div>

      {/* Gate card */}
      <div className="relative z-10 flex-1 flex items-center justify-center px-5 py-8">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className="w-full max-w-md rounded-[28px] yonder-cream-card shadow-cream-card border border-white/40 px-7 py-9 text-center"
        >
          <div className="w-16 h-16 mx-auto rounded-2xl bg-sun-ultra border-2 border-sun-light flex items-center justify-center text-3xl mb-5 shadow-sun">
            ✉️
          </div>

          <h1 className="font-black text-ink text-2xl leading-tight mb-2">
            Unlock our family adventure
          </h1>
          <p className="text-[13px] font-semibold text-ink-3 leading-relaxed mb-6 max-w-sm mx-auto">
            Enter your email and we&rsquo;ll craft a personalised narrative itinerary for{' '}
            <span className="text-sun font-black">{dest.name}</span> — every deal, voucher and
            saving woven in. Plus a beautiful journey card to take with you.
          </p>

          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2.5">
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setTouched(true)}
              placeholder="your@email.com"
              className={`flex-1 bg-white border-2 rounded-full px-4 py-3 text-[14px] font-semibold text-ink placeholder-ink-3/50 outline-none transition-all ${
                touched && !valid ? 'border-rose' : 'border-[#F0EBE3] focus:border-sun'
              }`}
            />
            <motion.button
              type="submit"
              disabled={submitting}
              whileHover={{ y: -1 }}
              whileTap={{ scale: 0.97 }}
              className="bg-gradient-to-r from-sun to-sun-light text-white font-black text-sm rounded-full px-6 py-3 shadow-glow-sun hover:shadow-sun-lg transition-shadow disabled:opacity-70 whitespace-nowrap"
            >
              {submitting ? 'Building…' : 'Build it →'}
            </motion.button>
          </form>
          {touched && !valid && (
            <p className="text-[11px] font-bold text-rose mt-2">Please enter a valid email address.</p>
          )}

          <p className="text-[10px] font-semibold text-ink-3/70 mt-4">
            Private · No spam · Just your journey and exclusive partner offers
          </p>
        </motion.div>
      </div>
    </div>
  )
}
